import {
  Box, Typography, useTheme, Breadcrumbs, MenuItem,
  TextField, Button, Menu,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useState } from "react";
import moment from "moment";

import AssessmentIcon  from "@mui/icons-material/Assessment";
import DownloadIcon    from "@mui/icons-material/Download";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FilterListIcon  from "@mui/icons-material/FilterList";
import ReceiptIcon     from "@mui/icons-material/Receipt";

import { useItemPurchaseSummativeReportList } from "api/hooks/reports";
import { PDFDownloadLink } from "@react-pdf/renderer";
import ItemPurchaseSummativeReportPdf from "scenes/reports/pdfForms/purchase/itemPurchaseSummativeReportPdf";
import { currencyFormatter } from "utilities/currencyFormatter";
import ReportTable, { RRow, RCell } from "components/Tables/ReportTable";

const PERIODS = ["TODAY", "THIS WEEK", "THIS MONTH", "CUSTOM"];

export default function ItemPurchaseSummativeReport() {
  const navigate           = useNavigate();
  const theme              = useTheme();
  const isDark             = theme.palette.mode === "dark";
  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);
  const user               = useSelector((s) => s.authReducer.user);

  const { itemPurchaseSummativeReportList, isLoadingItemPurchaseSummativeReportList, setFilter } =
    useItemPurchaseSummativeReportList(1, business_branch_id);

  /* colours */
  const bodyBg  = theme.palette.background.alt;
  const cardBg  = isDark ? "rgba(255,255,255,0.02)" : "#ffffff";
  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;
  const border  = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.15)";

  /* filter state */
  const [showCustom, setShowCustom]   = useState(false);
  const [startDate, setStartDate]     = useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [endDate, setEndDate]         = useState(moment().endOf("month").format("YYYY-MM-DD"));
  const [activePeriod, setActivePeriod] = useState("THIS MONTH");
  const report_period                 = `${startDate} to ${endDate}`;

  const handlePeriod = (val) => {
    setActivePeriod(val);
    if (val === "TODAY")      { setStartDate(moment().format("YYYY-MM-DD")); setEndDate(moment().format("YYYY-MM-DD")); setShowCustom(false); }
    if (val === "THIS WEEK")  { setStartDate(moment().startOf("week").format("YYYY-MM-DD")); setEndDate(moment().endOf("week").format("YYYY-MM-DD")); setShowCustom(false); }
    if (val === "THIS MONTH") { setStartDate(moment().startOf("month").format("YYYY-MM-DD")); setEndDate(moment().endOf("month").format("YYYY-MM-DD")); setShowCustom(false); }
    if (val === "CUSTOM")     { setShowCustom(true); }
  };

  /* export menu */
  const [exportAnchor, setExportAnchor] = useState(null);
  const exportOpen = Boolean(exportAnchor);

  /* table columns */
  const columns = [
    { label: "Item",                    width: 200 },
    { label: "Description",             width: 220 },
    { label: "Total Qty Purchased",     align: "center" },
    { label: "Unit" },
    { label: "Total Cost Price",        align: "right" },
    { label: "Total Discount",          align: "right" },
  ];

  /* shared input style */
  const inputSx = {
    "& .MuiInputBase-root": {
      fontSize: "13px", height: "36px",
      backgroundColor: cardBg,
      borderRadius: "8px",
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: border },
    "& .MuiInputLabel-root": { fontSize: "12px" },
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: bodyBg, p: "1.5rem 2rem" }}>

      {/* ── Breadcrumb + header ── */}
      <Box sx={{ mb: "1.75rem" }}>
        <Breadcrumbs
          separator={<ChevronRightIcon sx={{ fontSize: "14px", color: border }} />}
          sx={{ mb: "0.5rem" }}
        >
          <Typography
            onClick={() => navigate("/reports")}
            sx={{ fontSize: "12px", color: accent, cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", "&:hover": { textDecoration: "underline" } }}
          >
            <AssessmentIcon sx={{ fontSize: "14px" }} /> Reports
          </Typography>
          <Typography sx={{ fontSize: "12px", color: textSec }}>
            Item Purchase Summative Report
          </Typography>
        </Breadcrumbs>

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Box sx={{
              width: "38px", height: "38px", borderRadius: "10px",
              backgroundColor: "rgba(29,158,117,0.12)",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: accent, flexShrink: 0,
            }}>
              <ReceiptIcon sx={{ fontSize: "20px" }} />
            </Box>
            <Box>
              <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "20px", fontWeight: 400, color: textPri, lineHeight: 1.2 }}>
                Item Purchase Summative Report
              </Typography>
              <Typography sx={{ fontSize: "12px", color: textSec, mt: "2px" }}>
                {report_period}
              </Typography>
            </Box>
          </Box>

          {/* Export button */}
          <Button
            onClick={(e) => setExportAnchor(e.currentTarget)}
            endIcon={<DownloadIcon sx={{ fontSize: "16px" }} />}
            sx={{
              fontSize: "12px", fontWeight: 500,
              px: "1rem", py: "0.45rem",
              borderRadius: "8px",
              border: `1px solid ${border}`,
              color: accent,
              backgroundColor: "rgba(29,158,117,0.06)",
              "&:hover": { backgroundColor: "rgba(29,158,117,0.12)" },
              textTransform: "none",
            }}
          >
            Export
          </Button>
          <Menu
            anchorEl={exportAnchor}
            open={exportOpen}
            onClose={() => setExportAnchor(null)}
            PaperProps={{
              sx: {
                mt: "6px", minWidth: "140px",
                backgroundColor: cardBg,
                border: `1px solid ${border}`,
                borderRadius: "10px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              },
            }}
          >
            <MenuItem
              onClick={() => setExportAnchor(null)}
              sx={{ fontSize: "13px", color: textPri, borderRadius: "6px", mx: "4px", "&:hover": { backgroundColor: "rgba(29,158,117,0.08)" } }}
            >
              <PDFDownloadLink
                document={
                  <ItemPurchaseSummativeReportPdf
                    viewData={{ report_detail: itemPurchaseSummativeReportList, report_period }}
                    printedBy={`${user.first_name} ${user.last_name}`}
                  />
                }
                fileName={`Item Purchase Summative Report ${report_period}.pdf`}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                Download PDF
              </PDFDownloadLink>
            </MenuItem>
            <MenuItem
              onClick={() => setExportAnchor(null)}
              sx={{ fontSize: "13px", color: textPri, borderRadius: "6px", mx: "4px", "&:hover": { backgroundColor: "rgba(29,158,117,0.08)" } }}
            >
              Download Excel
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* ── Filter bar ── */}
      <Box sx={{
        display: "flex", alignItems: "center", flexWrap: "wrap", gap: "0.75rem",
        p: "0.875rem 1rem",
        mb: "1.5rem",
        backgroundColor: cardBg,
        border: `1px solid ${border}`,
        borderRadius: "10px",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", color: accent, mr: "0.25rem" }}>
          <FilterListIcon sx={{ fontSize: "16px" }} />
          <Typography sx={{ fontSize: "12px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.07em" }}>
            Filter
          </Typography>
        </Box>

        {/* Period pills */}
        <Box sx={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
          {PERIODS.filter((p) => p !== "CUSTOM").map((p) => (
            <Box
              key={p}
              onClick={() => handlePeriod(p)}
              sx={{
                px: "0.75rem", py: "0.3rem",
                borderRadius: "20px",
                fontSize: "12px", fontWeight: 500,
                cursor: "pointer",
                border: `1px solid ${activePeriod === p ? accent : border}`,
                color: activePeriod === p ? accent : textSec,
                backgroundColor: activePeriod === p ? "rgba(29,158,117,0.1)" : "transparent",
                transition: "all 0.15s",
                "&:hover": { borderColor: accent, color: accent },
              }}
            >
              {p.charAt(0) + p.slice(1).toLowerCase().replace("_", " ")}
            </Box>
          ))}
          <Box
            onClick={() => handlePeriod("CUSTOM")}
            sx={{
              px: "0.75rem", py: "0.3rem",
              borderRadius: "20px",
              fontSize: "12px", fontWeight: 500,
              cursor: "pointer",
              border: `1px solid ${showCustom ? accent : border}`,
              color: showCustom ? accent : textSec,
              backgroundColor: showCustom ? "rgba(29,158,117,0.1)" : "transparent",
              transition: "all 0.15s",
              "&:hover": { borderColor: accent, color: accent },
            }}
          >
            Custom range
          </Box>
        </Box>

        {/* Custom date inputs */}
        {showCustom && (
          <>
            <TextField
              label="Start date" type="date" size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              inputProps={{ max: endDate }}
              sx={{ ...inputSx, width: "150px" }}
            />
            <TextField
              label="End date" type="date" size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              inputProps={{ min: startDate }}
              sx={{ ...inputSx, width: "150px" }}
            />
          </>
        )}

        <Button
          onClick={() => setFilter({ start: startDate, end: endDate })}
          sx={{
            fontSize: "12px", fontWeight: 500,
            px: "1rem", py: "0.35rem",
            borderRadius: "8px",
            backgroundColor: accent,
            color: "#fff",
            textTransform: "none",
            "&:hover": { backgroundColor: theme.palette.secondary[500] ?? "#0F6E56" },
          }}
        >
          Apply
        </Button>
      </Box>

      {/* ── Table ── */}
      <ReportTable
        columns={columns}
        rows={itemPurchaseSummativeReportList}
        loading={isLoadingItemPurchaseSummativeReportList}
        emptyMessage="No purchase data found for this period"
        renderRow={(row, index) => (
          <RRow key={row.id ?? index} index={index}>
            <RCell>{row.product_name}</RCell>
            <RCell>{row.description}</RCell>
            <RCell align="center">{row.quantity_purchased}</RCell>
            <RCell>{row.unit}</RCell>
            <RCell align="right">{currencyFormatter(parseFloat(Number(row.purchase_amount)))}</RCell>
            <RCell align="right">{currencyFormatter(parseFloat(Number(row.purchase_discount)))}</RCell>
          </RRow>
        )}
      />
    </Box>
  );
}